import { type FilterState, describeActiveRange } from './dashboardFilterUtils';
import { useDashboardFilters } from './useDashboardFilters';
import { DateRangeSection } from './filters/DateRangeSection';
import { CompanySection } from './filters/CompanySection';
import { SupplierSection } from './filters/SupplierSection';
import { ClassificationSection } from './filters/ClassificationSection';
import { CallOffSection } from './filters/CallOffSection';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { X, Save } from 'lucide-react';

// Re-export the shared date helpers so existing importers keep resolving them
// here after the CR-6352 split into dashboardFilterUtils / useDashboardFilters.
export { formatLocalDate, getLast12MonthsRange } from './dashboardFilterUtils';


interface DashboardFiltersProps {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  suppliers: Array<{ id: number; name: string; group?: number | null; country?: string | null }>;
  customers: Array<{ id: number; name: string }>;
  sites: Array<{ id: number; name: string; country?: string; group_id?: number | null; group?: number | null }>;
  warehouses: Array<{ id: number; name: string; site: number }>;
  supplierGroups: Array<{ id: number; name: string }>;
  commodityGroups: Array<{ id: number; name: string }>;
  commodityTypes: Array<{ id: number; name: string; commodity_group?: number }>;
  // CR-4349: Division filter options (only shown when > 0)
  divisions: Array<{ id: number; name: string }>;
  preferencesCurrency: string;
  onApplyFilters: () => void;
  onResetFilters: () => void;
  onShowSaveDialog: () => void;
  onAutoCollapse?: () => void;
  // CR-4800: hide purchasing/logistics filters on the Compliance page and
  // surface approval-stage + risk filters instead.
  isComplianceMode?: boolean;
}

/**
 * CR-6401: the dashboard filter panel, grouped into labelled sections.
 *
 * Previously four undifferentiated grid rows in which related filters were
 * scattered — Division alone in its own row, the Historical/Live toggle beside
 * the strategic-suppliers tickbox, Commodity Group and Commodity Type split
 * across rows despite cascading into one another. This composes four section
 * components (Date Range / Company / Supplier / Classification) and owns only
 * the footer. Filter behaviour, input types and every cascade are unchanged.
 */
export function DashboardFilters({
  filters,
  setFilters,
  suppliers,
  customers: _customers,
  sites,
  warehouses = [],
  supplierGroups = [],
  commodityGroups = [],
  commodityTypes = [],
  divisions = [],
  preferencesCurrency: _preferencesCurrency,
  onApplyFilters,
  onResetFilters,
  onShowSaveDialog,
  onAutoCollapse: _onAutoCollapse,
  isComplianceMode = false,
}: DashboardFiltersProps) {
  const {
    portalIsSupplier, scopedDivisions, isDivisionLocked, isSiteLocked, lockedDivisionValue,
    siteCountryOptions, supplierCountryOptions, filteredSites, filteredWarehouses,
    activeQuickRange, isLast12MonthsActive,
    setQuickDateRange, handleSiteCountryChange, handleSupplierCountryChange,
  } = useDashboardFilters({ filters, setFilters, sites, suppliers, warehouses, divisions });

  const rangeSummary = describeActiveRange(activeQuickRange, isLast12MonthsActive, filters);

  // The call-off basis toggle only means anything to a company that actually
  // has call-off suppliers. `switch_call_off_tab` already resolves to "this
  // company has at least one active relationship with call_off_enabled", so
  // the whole section is hidden rather than shown inert. Compliance mode has
  // no delivery metrics to re-base, so it stays out there too.
  const { isFeatureEnabled } = useMenuSettings();
  const showCallOffBasis = !isComplianceMode && isFeatureEnabled('switch_call_off_tab');

  // CR-4802: dynamic filter visibility — Warehouse and Company Country only
  // matter when the tenant's data actually varies along that axis.
  const showWarehouse = !isComplianceMode && warehouses.length > 0;
  const showCompanyCountry = siteCountryOptions.length > 1;

  return (
    <div className="border-t bg-card">
      <DateRangeSection
        filters={filters}
        setFilters={setFilters}
        activeQuickRange={activeQuickRange}
        isLast12MonthsActive={isLast12MonthsActive}
        setQuickDateRange={setQuickDateRange}
        showDataMode={!isComplianceMode}
      />

      <CompanySection
        filters={filters}
        setFilters={setFilters}
        divisions={divisions}
        scopedDivisions={scopedDivisions}
        isDivisionLocked={isDivisionLocked}
        lockedDivisionValue={lockedDivisionValue}
        isSiteLocked={isSiteLocked}
        siteCountryOptions={siteCountryOptions}
        filteredSites={filteredSites}
        filteredWarehouses={filteredWarehouses}
        showWarehouse={showWarehouse}
        showCountry={showCompanyCountry}
        // CR-6402: on the supplier portal this group describes the customers
        // the supplier trades with, so the supplier-view design labels it
        // "Customer" rather than "Company".
        title={portalIsSupplier ? 'Customer' : 'Company'}
        onSiteCountryChange={handleSiteCountryChange}
      />

      <SupplierSection
        filters={filters}
        setFilters={setFilters}
        suppliers={suppliers}
        supplierGroups={supplierGroups}
        supplierCountryOptions={supplierCountryOptions}
        onSupplierCountryChange={handleSupplierCountryChange}
        showSupplierCountry={!portalIsSupplier}
        showPurchasingFilters={!isComplianceMode}
      />

      <ClassificationSection
        filters={filters}
        setFilters={setFilters}
        commodityGroups={commodityGroups}
        commodityTypes={commodityTypes}
        showPurchasingFilters={!isComplianceMode}
        isComplianceMode={isComplianceMode}
      />

      {showCallOffBasis && (
        <CallOffSection filters={filters} setFilters={setFilters} />
      )}

      {/* Filter footer: Clear all (left), what the results cover (centre),
          Apply (right) — the diagram's layout. Save Filter is kept alongside
          Clear all: it isn't in the mockup but is existing functionality the
          ticket requires be preserved. */}
      <div className="border-t px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={onResetFilters}
              className="px-5 py-2.5 border border-border rounded-md hover:bg-muted flex items-center gap-2 font-medium transition-all"
            >
              <X className="h-4 w-4" />
              Clear all
            </button>
            <button
              onClick={onShowSaveDialog}
              className="px-5 py-2.5 border border-primary text-primary rounded-md hover:bg-primary/10 flex items-center gap-2 font-medium transition-all"
            >
              <Save className="h-4 w-4" />
              Save filter
            </button>
          </div>

          <p className="text-sm text-muted-foreground">Showing results for {rangeSummary}</p>

          <button
            onClick={onApplyFilters}
            className="px-6 py-2.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 font-medium shadow-sm transition-all"
          >
            Apply filters
          </button>
        </div>
      </div>
    </div>
  );
}
